import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Upload, Trash2, Eye, EyeOff, Film, Users2, X, Link as LinkIcon, Pencil, RefreshCw } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import VideoPlayer from "@/components/VideoPlayer";
import VideoThumbnail from "@/components/VideoThumbnail";

function fmtSize(bytes) {
  if (!bytes) return "—";
  const mb = bytes / (1024 * 1024);
  return mb < 1 ? `${(bytes / 1024).toFixed(0)} KB` : `${mb.toFixed(1)} MB`;
}
function fmtDuration(s) {
  if (!s) return "—";
  const m = Math.floor(s / 60); const r = Math.floor(s % 60);
  return `${m}:${r.toString().padStart(2, "0")}`;
}

async function readVideoDuration(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => {
      const d = v.duration;
      URL.revokeObjectURL(url);
      if (!isFinite(d)) resolve(null); else resolve(Math.round(d));
    };
    v.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not read video metadata")); };
    v.src = url;
  });
}

// Generate a thumbnail JPEG blob from a video file (~10% into the video).
async function generateThumbnail(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    v.preload = "metadata";
    v.muted = true;
    v.playsInline = true;
    let seeked = false;
    v.onloadedmetadata = () => {
      const seekTime = Math.min(Math.max(v.duration * 0.1, 0.2), 3);
      v.currentTime = seekTime;
    };
    v.onseeked = () => {
      if (seeked) return; seeked = true;
      try {
        const targetWidth = 640;
        const ratio = v.videoHeight / (v.videoWidth || 1);
        const canvas = document.createElement("canvas");
        canvas.width = targetWidth;
        canvas.height = Math.round(targetWidth * (isFinite(ratio) && ratio > 0 ? ratio : 9 / 16));
        const ctx = canvas.getContext("2d");
        ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          URL.revokeObjectURL(url);
          resolve(blob || null);
        }, "image/jpeg", 0.82);
      } catch { URL.revokeObjectURL(url); resolve(null); }
    };
    v.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    v.src = url;
  });
}

function UploadDialog({ exercises, onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [exerciseId, setExerciseId] = useState("");
  const [isPublished, setIsPublished] = useState(true);
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState(null); // 'uploading' | 'processing'
  const [busy, setBusy] = useState(false);

  const onFile = async (f) => {
    setFile(f);
    if (!title && f?.name) setTitle(f.name.replace(/\.[^.]+$/, ""));
  };

  const submit = async (e) => {
    e.preventDefault();
    // YouTube path: teacher pasted a URL — save without file upload
    if (youtubeUrl.trim()) {
      if (!title.trim()) { toast.error("Please add a title for the YouTube video."); return; }
      setBusy(true);
      try {
        await api.post("/admin/videos/youtube", {
          url: youtubeUrl.trim(),
          title: title.trim(),
          description,
          exercise_id: exerciseId || undefined,
          is_published: isPublished,
        });
        toast.success("YouTube video added");
        onUploaded();
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally { setBusy(false); }
      return;
    }
    if (!file) { toast.error("Please choose a video file or paste a YouTube URL."); return; }
    if (file.size > 500 * 1024 * 1024) { toast.error("File too large — max 500 MB."); return; }
    setBusy(true); setProgress(0); setStage("uploading");
    try {
      let duration = null;
      try { duration = await readVideoDuration(file); } catch { /* ignore */ }
      if (duration && duration > 900) {
        toast.error(`Videos must be 15 minutes or less (this file is ${Math.floor(duration/60)}m ${duration%60}s).`);
        setBusy(false); setStage(null); return;
      }

      // Client-side thumbnail (best-effort)
      let thumb = null;
      try { thumb = await generateThumbnail(file); } catch { /* skip */ }

      // 1. Init upload session
      const { data: initData } = await api.post("/admin/videos/upload/init", { filename: file.name });
      const uploadId = initData.upload_id;
      const chunkSize = initData.chunk_size || 5 * 1024 * 1024;

      // 2. Upload chunks sequentially — each request is tiny and won't hit proxy timeouts
      const totalChunks = Math.ceil(file.size / chunkSize);
      for (let i = 0; i < totalChunks; i++) {
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const blob = file.slice(start, end);
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("chunk", blob, "chunk");
        await api.post("/admin/videos/upload/chunk", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setProgress(Math.round(((i + 1) / totalChunks) * 100));
      }

      // 3. Finalize — kicks off background storage transfer
      setStage("processing");
      const fd2 = new FormData();
      fd2.append("upload_id", uploadId);
      fd2.append("title", title || file.name);
      fd2.append("description", description);
      if (exerciseId) fd2.append("exercise_id", exerciseId);
      fd2.append("is_published", isPublished ? "true" : "false");
      if (duration) fd2.append("duration_seconds", String(duration));
      if (thumb) fd2.append("thumbnail", thumb, "thumb.jpg");
      await api.post("/admin/videos/upload/finalize", fd2, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // 4. Poll status until done or error (max ~30 min for large HEVC transcodes)
      let done = false;
      for (let tries = 0; tries < 900 && !done; tries++) {
        await new Promise(r => setTimeout(r, 2000));
        const { data: st } = await api.get(`/admin/videos/upload/status/${uploadId}`);
        if (st.status === "transcoding") setStage("transcoding");
        if (st.status === "done") { done = true; break; }
        if (st.status === "error") throw new Error(st.error || "Storage upload failed");
      }
      if (!done) throw new Error("Upload is taking too long. It may still finish — refresh in a minute.");

      toast.success("Video uploaded");
      onUploaded();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); setStage(null); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 card-tactile" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-heading text-2xl font-bold text-ink">Upload lesson video</h3>
          <button data-testid="close-upload-dialog" onClick={onClose} className="text-stone hover:text-ink"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="video-upload-form">
          <div className="p-3 rounded-2xl bg-lemon/30 border-2 border-lemon">
            <label className="block text-sm font-bold text-ink mb-1">YouTube link (recommended)</label>
            <input
              data-testid="video-youtube-input"
              type="url"
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              placeholder="https://youtube.com/watch?v=..."
              className="w-full h-12 px-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none bg-white"
            />
            <div className="text-xs text-stone mt-1">Upload the video to YouTube as Unlisted, paste the link here.</div>
          </div>
          <div>
            <label className="block text-sm font-bold text-ink mb-1">Or upload a video file (MP4, WebM, MOV — up to 15 min / 500 MB)</label>
            <input
              data-testid="video-file-input"
              type="file"
              accept="video/mp4,video/webm,video/quicktime,video/ogg,video/x-m4v,.mp4,.webm,.mov,.ogg,.m4v"
              onChange={(e) => onFile(e.target.files?.[0])}
              className="w-full text-sm file:mr-4 file:py-3 file:px-5 file:rounded-full file:border-0 file:bg-sky file:text-white file:font-bold file:cursor-pointer hover:file:bg-sky-hover"
            />
            {file && <div className="text-xs text-stone mt-1">{file.name} · {fmtSize(file.size)}</div>}
            <div className="text-xs text-stone mt-1">
              iPhone videos (HEVC) will be auto-converted to H.264 — this can add a few minutes to the upload.
            </div>
          </div>
          <div>
            <label className="block text-sm font-bold text-ink mb-1">Title</label>
            <input data-testid="video-title-input" required value={title} onChange={(e) => setTitle(e.target.value)} className="w-full h-12 px-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none" />
          </div>
          <div>
            <label className="block text-sm font-bold text-ink mb-1">Description (optional)</label>
            <textarea data-testid="video-description-input" value={description} onChange={(e) => setDescription(e.target.value)} className="w-full min-h-[70px] px-4 py-3 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none" />
          </div>
          <div>
            <label className="block text-sm font-bold text-ink mb-1">Link to exercise (optional)</label>
            <select data-testid="video-exercise-select" value={exerciseId} onChange={(e) => setExerciseId(e.target.value)} className="w-full h-12 px-3 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none">
              <option value="">— None (library only) —</option>
              {exercises.map((e) => <option key={e.id} value={e.id}>{e.title}</option>)}
            </select>
          </div>
          <label className="inline-flex items-center gap-3 p-3 rounded-2xl bg-canvas cursor-pointer">
            <input data-testid="video-publish-checkbox" type="checkbox" checked={isPublished} onChange={(e) => setIsPublished(e.target.checked)} className="w-5 h-5 accent-watermelon" />
            <span className="font-semibold text-ink text-sm">Publish immediately (students can watch)</span>
          </label>
          {busy && (
            <div className="space-y-2">
              <div className="w-full h-3 bg-cloud rounded-full overflow-hidden">
                <div data-testid="upload-progress" className={`h-full transition-all ${stage === "processing" || stage === "transcoding" ? "bg-lemon animate-pulse" : "bg-sky"}`} style={{ width: (stage === "processing" || stage === "transcoding") ? "100%" : `${progress}%` }} />
              </div>
              <div className="text-xs font-semibold text-stone text-center">
                {stage === "transcoding"
                  ? "Converting iPhone video to H.264… (this can take several minutes)"
                  : stage === "processing"
                  ? "Uploaded! Saving to library… (this can take a minute for large files)"
                  : `Uploading… ${progress}%`}
              </div>
            </div>
          )}
          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={onClose} disabled={busy} className="h-12 px-5 rounded-full bg-white border-2 border-cloud text-ink font-bold btn-tactile">Cancel</button>
            <button data-testid="video-upload-submit" type="submit" disabled={busy} className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-watermelon text-white font-bold btn-tactile hover:bg-watermelon-hover disabled:opacity-70">
              <Upload className="w-4 h-4" /> {busy ? (stage === "processing" ? "Finishing…" : `Uploading… ${progress}%`) : "Upload"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditDialog({ video, exercises, onClose, onSaved }) {
  const [title, setTitle] = useState(video.title);
  const [description, setDescription] = useState(video.description || "");
  const [exerciseId, setExerciseId] = useState(video.exercise_id || "");
  const [isPublished, setIsPublished] = useState(!!video.is_published);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/admin/videos/${video.id}`, { title, description, exercise_id: exerciseId, is_published: isPublished });
      toast.success("Video updated");
      onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-lg w-full p-6 card-tactile" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-heading text-2xl font-bold text-ink">Edit video</h3>
          <button onClick={onClose} className="text-stone"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="video-edit-form">
          <div>
            <label className="block text-sm font-bold text-ink mb-1">Title</label>
            <input data-testid="edit-video-title" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full h-12 px-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none" />
          </div>
          <div>
            <label className="block text-sm font-bold text-ink mb-1">Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} className="w-full min-h-[70px] px-4 py-3 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none" />
          </div>
          <div>
            <label className="block text-sm font-bold text-ink mb-1">Linked exercise</label>
            <select data-testid="edit-video-exercise" value={exerciseId} onChange={(e) => setExerciseId(e.target.value)} className="w-full h-12 px-3 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none">
              <option value="">— None —</option>
              {exercises.map((ex) => <option key={ex.id} value={ex.id}>{ex.title}</option>)}
            </select>
          </div>
          <label className="inline-flex items-center gap-3 p-3 rounded-2xl bg-canvas cursor-pointer">
            <input data-testid="edit-video-publish" type="checkbox" checked={isPublished} onChange={(e) => setIsPublished(e.target.checked)} className="w-5 h-5 accent-watermelon" />
            <span className="font-semibold text-ink text-sm">Published</span>
          </label>
          <div className="flex gap-3 justify-end">
            <button type="button" onClick={onClose} className="h-12 px-5 rounded-full bg-white border-2 border-cloud text-ink font-bold btn-tactile">Cancel</button>
            <button data-testid="edit-video-submit" type="submit" disabled={busy} className="h-12 px-6 rounded-full bg-watermelon text-white font-bold btn-tactile hover:bg-watermelon-hover disabled:opacity-70">
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ViewsDialog({ video, onClose }) {
  const [views, setViews] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/admin/videos/${video.id}/views`);
        setViews(data || []);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, [video.id]);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-6 card-tactile" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-heading text-2xl font-bold text-ink">Viewers · {video.title}</h3>
          <button onClick={onClose} className="text-stone"><X className="w-5 h-5" /></button>
        </div>
        {loading ? <div className="text-stone">Loading…</div> : views.length === 0 ? (
          <div className="text-stone text-center py-6">No one has watched this yet.</div>
        ) : (
          <ul className="divide-y divide-cloud" data-testid="views-list">
            {views.map((v) => (
              <li key={v.id} className="py-3 flex items-center justify-between gap-3">
                <div>
                  <div className="font-semibold text-ink">{v.student_name}</div>
                  <div className="text-xs text-stone">{v.student_email}</div>
                </div>
                <div className="text-xs text-stone whitespace-nowrap">{new Date(v.watched_at).toLocaleString()}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default function AdminVideos() {
  const [videos, setVideos] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewsFor, setViewsFor] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [v, e] = await Promise.all([api.get("/admin/videos"), api.get("/admin/exercises")]);
      setVideos(v.data || []);
      setExercises(e.data || []);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const togglePublish = async (v) => {
    try {
      await api.put(`/admin/videos/${v.id}`, { is_published: !v.is_published });
      toast.success(!v.is_published ? "Published" : "Unpublished");
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete "${v.title}"?`)) return;
    try {
      await api.delete(`/admin/videos/${v.id}`);
      toast.success("Video deleted");
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const reencode = async (v) => {
    if (!window.confirm(`Re-encode "${v.title}" to H.264 for browser playback? This can take several minutes for large files. Students may see a brief interruption during the swap.`)) return;
    try {
      await api.post(`/admin/videos/${v.id}/reencode`);
      toast.success("Re-encoding started. You can leave this page — refresh in a few minutes.");
      // Poll status
      const start = Date.now();
      while (Date.now() - start < 30 * 60 * 1000) {
        await new Promise(r => setTimeout(r, 5000));
        try {
          const { data: st } = await api.get(`/admin/videos/${v.id}/reencode/status`);
          if (st.reencode_status === "done") { toast.success("Re-encode complete — video should now play in all browsers."); await load(); return; }
          if (st.reencode_status === "skipped") { toast.info(`Skipped — ${st.reencode_msg || "already browser-safe"}.`); await load(); return; }
          if (st.reencode_status === "error") { toast.error(`Re-encode failed: ${st.reencode_msg || "unknown"}`); return; }
        } catch { /* ignore transient */ }
      }
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  return (
    <AppLayout variant="admin">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-ink">Videos</h1>
            <p className="text-stone mt-1">Upload lesson videos for your students to watch.</p>
          </div>
          <button data-testid="upload-video-button" onClick={() => setShowUpload(true)} className="inline-flex items-center gap-2 h-12 px-5 rounded-full bg-watermelon text-white font-bold btn-tactile hover:bg-watermelon-hover">
            <Plus className="w-5 h-5" /> Upload Video
          </button>
        </div>

        {loading ? (
          <div className="text-stone">Loading…</div>
        ) : videos.length === 0 ? (
          <div data-testid="videos-empty" className="p-10 bg-white rounded-3xl card-tactile text-center">
            <Film className="w-10 h-10 text-pebble mx-auto mb-3" />
            <div className="font-heading text-lg font-bold text-ink">No videos yet</div>
            <div className="text-stone text-sm">Upload your first lesson to get started.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="videos-grid">
            {videos.map((v) => (
              <div key={v.id} data-testid={`video-card-${v.id}`} className="bg-white rounded-3xl card-tactile flex flex-col overflow-hidden">
                <div className="aspect-video relative">
                  <VideoThumbnail videoId={v.id} youtubeId={v.youtube_id} hasThumbnail={v.has_thumbnail} className="w-full h-full" />
                  <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded-md bg-black/70 text-white text-xs font-bold">
                    {fmtDuration(v.duration_seconds)}
                  </span>
                </div>
                <div className="p-5 flex flex-col flex-1">
                <div className="flex items-start justify-between mb-3">
                  <div className="inline-flex items-center gap-2 flex-wrap">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${v.is_published ? "bg-leaf/15 text-leaf" : "bg-lemon/40 text-ink"}`}>
                      {v.is_published ? "Published" : "Draft"}
                    </span>
                    {v.exercise_title && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-sky-light text-sky-hover text-xs font-bold">
                        <LinkIcon className="w-3 h-3" /> {v.exercise_title}
                      </span>
                    )}
                  </div>
                </div>
                <h3 className="font-heading text-xl font-bold text-ink mb-1 line-clamp-2">{v.title}</h3>
                {v.description && <p className="text-sm text-stone mb-3 line-clamp-2">{v.description}</p>}
                <div className="text-xs text-stone mb-4">{fmtSize(v.size)} · {v.view_count ?? 0} view{v.view_count === 1 ? "" : "s"}</div>
                <div className="mt-auto flex flex-wrap gap-2">
                  <button data-testid={`preview-video-${v.id}`} onClick={() => setPreview(v)} className="inline-flex items-center gap-1 h-10 px-3 rounded-full bg-sky text-white font-bold text-sm btn-tactile hover:bg-sky-hover"><Film className="w-4 h-4" /> Watch</button>
                  <button data-testid={`edit-video-${v.id}`} onClick={() => setEditing(v)} className="inline-flex items-center gap-1 h-10 px-3 rounded-full bg-white border-2 border-cloud text-ink font-bold text-sm btn-tactile hover:border-sky"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`toggle-publish-video-${v.id}`} onClick={() => togglePublish(v)} className="inline-flex items-center gap-1 h-10 px-3 rounded-full bg-white border-2 border-cloud text-ink font-bold text-sm btn-tactile hover:border-sky">
                    {v.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button data-testid={`video-views-${v.id}`} onClick={() => setViewsFor(v)} className="inline-flex items-center gap-1 h-10 px-3 rounded-full bg-white border-2 border-cloud text-ink font-bold text-sm btn-tactile hover:border-sky"><Users2 className="w-4 h-4" /></button>
                  {!v.youtube_id && (
                    <button data-testid={`reencode-video-${v.id}`} onClick={() => reencode(v)} title="Re-encode to H.264 (fix iPhone/HEVC playback)" className="inline-flex items-center gap-1 h-10 px-3 rounded-full bg-white border-2 border-cloud text-ink font-bold text-sm btn-tactile hover:border-sky"><RefreshCw className="w-4 h-4" /></button>
                  )}
                  <button data-testid={`delete-video-${v.id}`} onClick={() => remove(v)} className="inline-flex items-center gap-1 h-10 px-3 rounded-full bg-watermelon-light text-watermelon font-bold text-sm btn-tactile hover:bg-watermelon hover:text-white"><Trash2 className="w-4 h-4" /></button>
                </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {showUpload && <UploadDialog exercises={exercises} onClose={() => setShowUpload(false)} onUploaded={() => { setShowUpload(false); load(); }} />}
        {editing && <EditDialog video={editing} exercises={exercises} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
        {viewsFor && <ViewsDialog video={viewsFor} onClose={() => setViewsFor(null)} />}
        {preview && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
            <div className="bg-white rounded-3xl max-w-2xl w-full p-4 card-tactile" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-heading text-lg font-bold text-ink">{preview.title}</h3>
                <button onClick={() => setPreview(null)} className="text-stone"><X className="w-5 h-5" /></button>
              </div>
              <VideoPlayer videoId={preview.id} youtubeId={preview.youtube_id} autoPlay />
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
